import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, HttpResponse } from '@wix/sdk-types';
import * as ambassadorWixEcomV1PickupLocation from './ecom-v1-pickup-location-pickup-locations.http.js';
// @ts-ignore
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

/** PickupLocation is the main entity of PickupLocations that can be used for lorem ipsum dolor */
export interface PickupLocation {
  /**
   * PickupLocation ID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Represents the time this PickupLocation was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this PickupLocation was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Pickup Location Name */
  name?: string | null;
  /** Pickup Location Address */
  address?: Address;
  /** Expected delivery time in free text. For example, `"3-5 business days"`. */
  deliveryTime?: string | null;
  /** Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`. */
  instructions?: string | null;
  /** inactive pickup locations should not be shown in checkout */
  active?: boolean | null;
  /** at runtime for a given pickup input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one. */
  rates?: ConditionalRates[];
  /** This pickup location is active for the following delivery regions. */
  deliveryRegionIds?: string[];
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  addressLine1?: string | null;
  /** Country code. */
  country?: string | null;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  addressLine2?: string | null;
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  addressLine?: string | null;
}

export interface StreetAddress {
  number?: string;
  name?: string;
}

export interface ConditionalRates {
  /**
   * there is an AND logic between all the conditions. Empty conditions means true.
   * For example: weight > 0 and weight <= 10
   */
  conditions?: Condition[];
  /** The amount of the rate that will be returned if all conditions are met. */
  amount?: string;
  /** When this flag is set to true, multiply the amount by the number of line items passed on the request. */
  multiplyByQuantity?: boolean;
}

export interface Condition {
  type?: ConditionType;
  /**
   * The value in respective to the condition type
   * Weight values should be in the same weight units of the store: KG / LB
   * Total price is according to the store currency
   * Quantity of items should be integers
   */
  value?: string;
  operator?: LogicalOperator;
}

export enum ConditionType {
  UNKNOWN_TYPE = 'UNKNOWN_TYPE',
  BY_TOTAL_WEIGHT = 'BY_TOTAL_WEIGHT',
  BY_TOTAL_PRICE = 'BY_TOTAL_PRICE',
  BY_TOTAL_QUANTITY = 'BY_TOTAL_QUANTITY',
}

export enum LogicalOperator {
  UNKNOWN_LOGICAL_OPERATOR_TYPE = 'UNKNOWN_LOGICAL_OPERATOR_TYPE',
  EQ = 'EQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
}

export interface DiffmatokyPayload {
  left?: string;
  right?: string;
  compareChannel?: string;
  entityId?: string;
  errorInformation?: ErrorInformation;
  tags?: string[];
}

export interface ErrorInformation {
  stackTrace?: string;
}

export interface CreatePickupLocationRequest {
  /** PickupLocation to be created */
  pickupLocation: PickupLocation;
}

export interface CreatePickupLocationResponse {
  /** The created PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface GetPickupLocationRequest {
  /** Id of the PickupLocation to retrieve */
  pickupLocationId: string;
}

export interface GetPickupLocationResponse {
  /** The retrieved PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface UpdatePickupLocationRequest {
  /** PickupLocation to be updated, may be partial */
  pickupLocation: PickupLocation;
}

export interface UpdatePickupLocationResponse {
  /** The updated PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface DeletePickupLocationRequest {
  /** Id of the PickupLocation to delete */
  pickupLocationId: string;
}

export interface DeletePickupLocationResponse {}

export interface QueryPickupLocationRequest {
  /** WQL expression */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrder;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /** Maximum number of items to return in the results. */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryPickupLocationResponse {
  /** The retrieved PickupLocations */
  pickupLocations?: PickupLocation[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor string pointing to the next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to the previous page in the list of results. */
  prev?: string | null;
}

export interface AddDeliveryRegionRequest {
  /** Id of the PickupLocation to add to the delivery region */
  pickupLocationId: string;
  /** Id of the DeliveryRegion to add the PickupLocation to */
  deliveryRegionId: string;
  /** Revision of the PickupLocation */
  revision: string | null;
}

export interface AddDeliveryRegionResponse {
  /** The updated PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface RemoveDeliveryRegionRequest {
  /** Id of the PickupLocation to add to the delivery region */
  pickupLocationId: string;
  /** Id of the DeliveryRegion to add the PickupLocation to */
  deliveryRegionId: string;
  /** Revision of the PickupLocation */
  revision: string | null;
}

export interface RemoveDeliveryRegionResponse {
  /** The updated PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface BulkCreatePickupLocationRequest {
  pickupLocations: PickupLocation[];
}

export interface BulkCreatePickupLocationResponse {
  pickupLocations?: PickupLocation[];
  errors?: PickupLocationError[];
}

export interface PickupLocationError {
  _id?: string;
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdatePickupLocationRequest {
  pickupLocations: PickupLocation[];
}

export interface BulkUpdatePickupLocationResponse {
  pickupLocations?: PickupLocation[];
  errors?: PickupLocationError[];
}

export interface BulkDeletePickupLocationRequest {
  pickupLocationIds: string[];
}

export interface BulkDeletePickupLocationResponse {
  errors?: PickupLocationError[];
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /**
   * Unique event ID.
   * Allows clients to ignore duplicate webhooks.
   */
  _id?: string;
  /**
   * Assumes actions are also always typed to an entity_type
   * Example: wix.stores.catalog.product, wix.bookings.session, wix.payments.transaction
   */
  entityFqdn?: string;
  /**
   * This is top level to ease client code dispatching of messages (switch on entity_fqdn+slug)
   * This is although the created/updated/deleted notion is duplication of the oneof types
   * Example: created/updated/deleted/started/completed/email_opened
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example: 2020-04-26T13:57:50.699Z */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number defining the order of updates to the underlying entity.
   * For example, given that some entity was updated at 16:00 and than again at 16:01,
   * it is guaranteed that the sequence number of the second update is strictly higher than the first.
   * As the consumer, you can use this value to ensure that you handle messages in the correct order.
   * To do so, you will need to persist this number on your end, and compare the sequence number from the
   * message against the one you have stored. Given that the stored number is higher, you should ignore the message.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /** App instance ID. */
  instanceId?: string | null;
  /** Event type. */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /** ID of a site visitor that has not logged in to the site. */
  anonymousVisitorId?: string;
  /** ID of a site visitor that has logged in to the site. */
  memberId?: string;
  /** ID of a Wix user (site owner, contributor, etc.). */
  wixUserId?: string;
  /** ID of an app. */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /** ID of a site visitor that has not logged in to the site. */
  anonymousVisitorId?: string;
  /** ID of a site visitor that has logged in to the site. */
  memberId?: string;
  /** ID of a Wix user (site owner, contributor, etc.). */
  wixUserId?: string;
  /** ID of an app. */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

interface StreetAddressNonNullableFields {
  number: string;
  name: string;
  apt: string;
}

interface AddressNonNullableFields {
  streetAddress?: StreetAddressNonNullableFields;
}

interface ConditionNonNullableFields {
  type: ConditionType;
  value: string;
  operator: LogicalOperator;
}

interface ConditionalRatesNonNullableFields {
  conditions: ConditionNonNullableFields[];
  amount: string;
  multiplyByQuantity: boolean;
}

export interface PickupLocationNonNullableFields {
  address?: AddressNonNullableFields;
  rates: ConditionalRatesNonNullableFields[];
  deliveryRegionIds: string[];
}

export interface CreatePickupLocationResponseNonNullableFields {
  pickupLocation?: PickupLocationNonNullableFields;
}

export interface GetPickupLocationResponseNonNullableFields {
  pickupLocation?: PickupLocationNonNullableFields;
}

export interface UpdatePickupLocationResponseNonNullableFields {
  pickupLocation?: PickupLocationNonNullableFields;
}

export interface QueryPickupLocationResponseNonNullableFields {
  pickupLocations: PickupLocationNonNullableFields[];
}

export interface AddDeliveryRegionResponseNonNullableFields {
  pickupLocation?: PickupLocationNonNullableFields;
}

export interface RemoveDeliveryRegionResponseNonNullableFields {
  pickupLocation?: PickupLocationNonNullableFields;
}

interface ApplicationErrorNonNullableFields {
  code: string;
  description: string;
}

interface PickupLocationErrorNonNullableFields {
  _id: string;
  error?: ApplicationErrorNonNullableFields;
}

export interface BulkCreatePickupLocationResponseNonNullableFields {
  pickupLocations: PickupLocationNonNullableFields[];
  errors: PickupLocationErrorNonNullableFields[];
}

export interface BulkUpdatePickupLocationResponseNonNullableFields {
  pickupLocations: PickupLocationNonNullableFields[];
  errors: PickupLocationErrorNonNullableFields[];
}

export interface BulkDeletePickupLocationResponseNonNullableFields {
  errors: PickupLocationErrorNonNullableFields[];
}

/**
 * Creates a new PickupLocation
 * @param pickupLocation - PickupLocation to be created
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocation
 * @requiredField pickupLocation.address
 * @permissionId ECOM.PICKUP_LOCATION_CREATE
 * @returns The created PickupLocation
 * @fqn wix.ecom.v1.PickupLocations.CreatePickupLocation
 */
export async function createPickupLocation(
  pickupLocation: PickupLocation
): Promise<PickupLocation & PickupLocationNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ pickupLocation: pickupLocation }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocation.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.createPickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )?.pickupLocation!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocation: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocation']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Get a PickupLocation by id
 * @param pickupLocationId - Id of the PickupLocation to retrieve
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_READ
 * @returns The retrieved PickupLocation
 * @fqn wix.ecom.v1.PickupLocations.GetPickupLocation
 */
export async function getPickupLocation(
  pickupLocationId: string
): Promise<PickupLocation & PickupLocationNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
  });

  const reqOpts = ambassadorWixEcomV1PickupLocation.getPickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )?.pickupLocation!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Update a PickupLocation
 * Delivery regions cannot be updated using this method, use AddDeliveryRegion and RemoveDeliveryRegion instead.
 * @param _id - PickupLocation ID
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField pickupLocation
 * @requiredField pickupLocation.revision
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @returns The updated PickupLocation
 * @fqn wix.ecom.v1.PickupLocations.UpdatePickupLocation
 */
export async function updatePickupLocation(
  _id: string | null,
  pickupLocation: UpdatePickupLocation
): Promise<PickupLocation & PickupLocationNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      pickupLocation: { ...pickupLocation, id: _id },
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocation.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.updatePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )?.pickupLocation!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { pickupLocation: '$[1]' },
        explicitPathsToArguments: { 'pickupLocation.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'pickupLocation']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdatePickupLocation {
  /**
   * PickupLocation ID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Represents the time this PickupLocation was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this PickupLocation was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Pickup Location Name */
  name?: string | null;
  /** Pickup Location Address */
  address?: Address;
  /** Expected delivery time in free text. For example, `"3-5 business days"`. */
  deliveryTime?: string | null;
  /** Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`. */
  instructions?: string | null;
  /** inactive pickup locations should not be shown in checkout */
  active?: boolean | null;
  /** at runtime for a given pickup input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one. */
  rates?: ConditionalRates[];
  /** This pickup location is active for the following delivery regions. */
  deliveryRegionIds?: string[];
}

/**
 * Delete a PickupLocation
 * @param pickupLocationId - Id of the PickupLocation to delete
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_DELETE
 * @fqn wix.ecom.v1.PickupLocations.DeletePickupLocation
 */
export async function deletePickupLocation(
  pickupLocationId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
  });

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.deletePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Query PickupLocations using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
 * @public
 * @documentationMaturity preview
 * @permissionId ECOM.PICKUP_LOCATION_READ
 * @fqn wix.ecom.v1.PickupLocations.QueryPickupLocation
 */
export function queryPickupLocation(): PickupLocationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    PickupLocation,
    'CURSOR',
    QueryPickupLocationRequest,
    QueryPickupLocationResponse
  >({
    func: async (payload: QueryPickupLocationRequest) => {
      const reqOpts =
        ambassadorWixEcomV1PickupLocation.queryPickupLocation(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPickupLocationRequest['query']) => {
      const args = [query, {}] as [QueryPickupLocationRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryPickupLocationResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [{ path: 'pickupLocations.address' }],
          },
        ])
      );

      return {
        items: transformedData?.pickupLocations,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PickupLocationsQueryResult extends QueryCursorResult {
  items: PickupLocation[];
  query: PickupLocationsQueryBuilder;
  next: () => Promise<PickupLocationsQueryResult>;
  prev: () => Promise<PickupLocationsQueryResult>;
}

export interface PickupLocationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName:
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city',
    value: string
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any[]
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'deliveryRegionIds',
    value: any[]
  ) => PickupLocationsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: boolean
  ) => PickupLocationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => PickupLocationsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => PickupLocationsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<PickupLocationsQueryResult>;
}

/**
 * Add a DeliveryRegion to a PickupLocation
 * @param pickupLocationId - Id of the PickupLocation to add to the delivery region
 * @param deliveryRegionId - Id of the DeliveryRegion to add the PickupLocation to
 * @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options
 * @requiredField options.revision
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @fqn wix.ecom.v1.PickupLocations.AddDeliveryRegion
 */
export async function addDeliveryRegion(
  pickupLocationId: string,
  deliveryRegionId: string,
  options: AddDeliveryRegionOptions
): Promise<
  AddDeliveryRegionResponse & AddDeliveryRegionResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
    deliveryRegionId: deliveryRegionId,
    revision: options?.revision,
  });

  const reqOpts = ambassadorWixEcomV1PickupLocation.addDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pickupLocationId: '$[0]',
          deliveryRegionId: '$[1]',
          revision: '$[2].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId', 'deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddDeliveryRegionOptions {
  /** Revision of the PickupLocation */
  revision: string | null;
}

/**
 * Remove a DeliveryRegion from a PickupLocation
 * @param pickupLocationId - Id of the PickupLocation to add to the delivery region
 * @param deliveryRegionId - Id of the DeliveryRegion to add the PickupLocation to
 * @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options
 * @requiredField options.revision
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @fqn wix.ecom.v1.PickupLocations.RemoveDeliveryRegion
 */
export async function removeDeliveryRegion(
  pickupLocationId: string,
  deliveryRegionId: string,
  options: RemoveDeliveryRegionOptions
): Promise<
  RemoveDeliveryRegionResponse & RemoveDeliveryRegionResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
    deliveryRegionId: deliveryRegionId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.removeDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pickupLocationId: '$[0]',
          deliveryRegionId: '$[1]',
          revision: '$[2].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId', 'deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemoveDeliveryRegionOptions {
  /** Revision of the PickupLocation */
  revision: string | null;
}

/**
 * Bulk Create for new PickupLocation
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocations
 * @requiredField pickupLocations.address
 * @permissionId ECOM.PICKUP_LOCATION_CREATE
 * @fqn wix.ecom.v1.PickupLocations.BulkCreatePickupLocation
 */
export async function bulkCreatePickupLocation(
  pickupLocations: PickupLocation[]
): Promise<
  BulkCreatePickupLocationResponse &
    BulkCreatePickupLocationResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ pickupLocations: pickupLocations }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocations.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.bulkCreatePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocations.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocations: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocations']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Update a PickupLocation
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocations
 * @requiredField pickupLocations._id
 * @requiredField pickupLocations.revision
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @fqn wix.ecom.v1.PickupLocations.BulkUpdatePickupLocation
 */
export async function bulkUpdatePickupLocation(
  pickupLocations: PickupLocation[]
): Promise<
  BulkUpdatePickupLocationResponse &
    BulkUpdatePickupLocationResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ pickupLocations: pickupLocations }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocations.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.bulkUpdatePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocations.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocations: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocations']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Delete a PickupLocation
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocationIds
 * @permissionId ECOM.PICKUP_LOCATION_DELETE
 * @fqn wix.ecom.v1.PickupLocations.BulkDeletePickupLocation
 */
export async function bulkDeletePickupLocation(
  pickupLocationIds: string[]
): Promise<
  BulkDeletePickupLocationResponse &
    BulkDeletePickupLocationResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationIds: pickupLocationIds,
  });

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.bulkDeletePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocationIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
